import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { teamsService, userService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../types/users';
import PageTitle from '../layout/PageTitle';
import TeamDelegations from '../teams/TeamDelegations';
import TeamSettings from '../teams/TeamSettings';

const TeamsManagement = () => {
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, team: null });
  const [delegationsDialog, setDelegationsDialog] = useState({ open: false, team: null });
  const [settingsDialog, setSettingsDialog] = useState({ open: false, team: null });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#2196F3',
    adminUserId: '',
    assistantAdminUserId: '', // Admin adjoint
    supportTeams: []
  });

  // Vérifier les permissions SuperAdmin
  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;

  useEffect(() => {
    if (isSuperAdmin) {
      loadData();
    }
  }, [isSuperAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsData, usersData] = await Promise.all([
        teamsService.getAll(),
        userService.getAll()
      ]);
      
      setTeams(teamsData);
      setUsers(usersData);
      setError('');
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name,
        description: team.description || '',
        color: team.color || '#2196F3',
        adminUserId: team.adminUserId || '',
        assistantAdminUserId: team.assistantAdminUserId || '',
        supportTeams: team.settings?.supportTeams || []
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        description: '',
        color: '#2196F3',
        adminUserId: '',
        assistantAdminUserId: '',
        supportTeams: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeam(null);
    setFormData({
      name: '',
      description: '',
      color: '#2196F3',
      adminUserId: '',
      supportTeams: []
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        setError('Le nom de l\'équipe est requis');
        return;
      }

      const teamData = {
        ...formData,
        name: formData.name.trim()
      };

      if (editingTeam) {
        await teamsService.update(editingTeam.id, teamData);
        setSuccess('Équipe mise à jour avec succès');
      } else {
        await teamsService.create(teamData);
        setSuccess('Équipe créée avec succès');
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Erreur sauvegarde équipe:', error);
      setError('Erreur lors de la sauvegarde de l\'équipe');
    }
  };

  const handleDelete = async (team) => {
    setDeleteConfirmDialog({ open: true, team });
  };

  const handleToggleAutoEmail = async (team) => {
    try {
      const currentEnabled = team.settings?.emailSettings?.enabled || false;
      const newEnabled = !currentEnabled;

      // Mettre à jour les paramètres de l'équipe
      const updatedSettings = {
        ...team.settings,
        emailSettings: {
          ...team.settings?.emailSettings,
          enabled: newEnabled
        }
      };

      await teamsService.updateSettings(team.id, updatedSettings);
      
      setSuccess(`Emails automatiques ${newEnabled ? 'activés' : 'désactivés'} pour l'équipe ${team.name}`);
      
      // Recharger les équipes pour afficher le nouveau statut
      loadData();
    } catch (err) {
      setError('Erreur lors de la modification: ' + err.message);
    }
  };

  const confirmDelete = async () => {
    try {
      await teamsService.delete(deleteConfirmDialog.team.id);
      setSuccess('Équipe supprimée avec succès');
      setDeleteConfirmDialog({ open: false, team: null });
      loadData();
    } catch (error) {
      console.error('Erreur suppression équipe:', error);
      setError('Erreur lors de la suppression de l\'équipe');
    }
  };

  const getAdminName = (adminUserId) => {
    const admin = users.find(u => u.id === adminUserId);
    return admin ? `${admin.firstName} ${admin.lastName}` : 'Non assigné';
  };

  const getDelegationsCount = (team) => {
    return team.delegations ? team.delegations.filter(d => d.isActive).length : 0;
  };

  if (!isSuperAdmin) {
    return (
      <Alert severity="error">
        Accès refusé. Seuls les Super Administrateurs peuvent gérer les équipes.
      </Alert>
    );
  }

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <PageTitle
        title="Gestion des Équipes"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF5722 30%, #E68900 90%)',
              }
            }}
          >
            Nouvelle Équipe
          </Button>
        }
      />

      {/* Messages */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Liste des équipes en cartes */}
      <Grid container spacing={3}>
        {teams.map((team) => (
          <Grid item xs={12} md={6} lg={4} key={team.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                  borderColor: team.color || '#FF6B35'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{ 
                      bgcolor: team.color, 
                      width: 48, 
                      height: 48, 
                      mr: 2,
                      border: '2px solid',
                      borderColor: 'background.paper',
                      boxShadow: 2
                    }}
                  >
                    <GroupIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {team.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      {team.description || 'Aucune description'}
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Admin: {getAdminName(team.adminUserId)}
                    </Typography>
                  </Box>
                  
                  {team.assistantAdminUserId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="secondary" />
                      <Typography variant="body2">
                        Admin adjoint: {getAdminName(team.assistantAdminUserId)}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Délégations: {getDelegationsCount(team)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Email: {team.settings?.emailSettings?.enabled ? 'Activé' : 'Désactivé'}
                    </Typography>
                  </Box>
                </Stack>

                {team.settings?.supportTeams?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Équipes de support:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {team.settings.supportTeams.map((supportTeamId) => {
                        const supportTeam = teams.find(t => t.id === supportTeamId);
                        return supportTeam ? (
                          <Chip
                            key={supportTeamId}
                            label={supportTeam.name}
                            size="small"
                            variant="outlined"
                          />
                        ) : null;
                      })}
                    </Box>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ pt: 1, pb: 2, px: 2, justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Modifier l'équipe">
                    <IconButton
                      onClick={() => handleOpenDialog(team)}
                      size="small"
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.light', color: 'white' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Paramètres de l'équipe">
                    <IconButton
                      onClick={() => setSettingsDialog({ open: true, team })}
                      size="small"
                      sx={{ 
                        color: 'info.main',
                        '&:hover': { bgcolor: 'info.light', color: 'white' }
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={team.settings?.emailSettings?.enabled ? "Emails automatiques activés - Cliquer pour désactiver" : "Emails automatiques désactivés - Cliquer pour activer"}>
                    <IconButton
                      onClick={() => handleToggleAutoEmail(team)}
                      size="small"
                      sx={{ 
                        color: team.settings?.emailSettings?.enabled ? 'success.main' : 'grey.400',
                        '&:hover': { 
                          bgcolor: team.settings?.emailSettings?.enabled ? 'success.light' : 'grey.200', 
                          color: 'white' 
                        }
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Gestion des délégations">
                    <IconButton
                      onClick={() => setDelegationsDialog({ open: true, team })}
                      size="small"
                      sx={{ 
                        color: 'secondary.main',
                        '&:hover': { bgcolor: 'secondary.light', color: 'white' }
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                
                <Tooltip title="Supprimer l'équipe">
                  <IconButton
                    onClick={() => handleDelete(team)}
                    size="small"
                    sx={{ 
                      color: 'error.main',
                      '&:hover': { bgcolor: 'error.light', color: 'white' }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {teams.length === 0 && (
        <Paper 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'divider',
            bgcolor: 'background.default'
          }}
        >
          <GroupIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
          <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
            Aucune équipe créée
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            Créez votre première équipe pour organiser vos techniciens et commencer la gestion des astreintes.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
              color: 'white',
              px: 4,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(45deg, #FF5722 30%, #E68900 90%)',
              }
            }}
          >
            Créer une équipe
          </Button>
        </Paper>
      )}

      {/* Dialog de création/modification */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTeam ? 'Modifier l\'équipe' : 'Nouvelle équipe'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Nom de l'équipe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Couleur"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  sx={{ width: '80px' }}
                />
                <PaletteIcon sx={{ color: formData.color }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Administrateur principal</InputLabel>
                <Select
                  value={formData.adminUserId}
                  onChange={(e) => setFormData({ ...formData, adminUserId: e.target.value })}
                  label="Administrateur principal"
                >
                  <MenuItem value="">
                    <em>Non assigné</em>
                  </MenuItem>
                  {users
                    .filter(user => user.role === USER_ROLES.ADMIN || user.role === USER_ROLES.SUPER_ADMIN)
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Administrateur adjoint (optionnel)</InputLabel>
                <Select
                  value={formData.assistantAdminUserId}
                  onChange={(e) => setFormData({ ...formData, assistantAdminUserId: e.target.value })}
                  label="Administrateur adjoint (optionnel)"
                >
                  <MenuItem value="">
                    <em>Non assigné</em>
                  </MenuItem>
                  {users
                    .filter(user => 
                      (user.role === USER_ROLES.ADMIN || user.role === USER_ROLES.SUPER_ADMIN) &&
                      user.id !== formData.adminUserId // Éviter qu'un admin soit principal ET adjoint
                    )
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTeam ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, team: null })}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'équipe "{deleteConfirmDialog.team?.name}" ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, team: null })}>
            Annuler
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de gestion des délégations */}
      <TeamDelegations
        open={delegationsDialog.open}
        onClose={() => setDelegationsDialog({ open: false, team: null })}
        team={delegationsDialog.team}
        onUpdate={(updatedTeam) => {
          // Mettre à jour l'équipe dans la liste
          setTeams(teams.map(t => t.id === updatedTeam.id ? updatedTeam : t));
          setSuccess('Délégations mises à jour avec succès');
        }}
      />

      {/* Dialog des paramètres d'équipe */}
      <TeamSettings
        open={settingsDialog.open}
        onClose={() => setSettingsDialog({ open: false, team: null })}
        team={settingsDialog.team}
        onUpdate={(updatedTeam) => {
          // Mettre à jour l'équipe dans la liste
          setTeams(teams.map(t => t.id === updatedTeam.id ? updatedTeam : t));
          setSuccess('Paramètres d\'équipe mis à jour avec succès');
        }}
      />
    </Box>
  );
};

export default TeamsManagement;